import { ImageLightbox } from "@/components/waste/image-lightbox";
import { RelabelPicker } from "@/components/waste/relabel-picker";
import { LogWasteDialog } from "@/components/waste/log-waste-dialog";
import { CommentsPanel } from "@/components/waste/comments-panel";
import { TransactionCard, isPlaceholderCapture } from "@/components/waste/transaction-card";
import type { SiteSummary } from "@/hooks/use-active-menus";
import type { WasteEventActionsController } from "@/hooks/use-waste-event-actions-controller";

const EMPTY_SITE_SET: ReadonlySet<string> = new Set();

/**
 * The shared waste-event overlays (POS2-849): image lightbox, relabel picker,
 * log/edit dialog, and comments panel. Driven entirely by the controller's
 * modal state so the Transactions list and the single-event view render one
 * identical set. Page-specific bits (sites for the log dialog, comment
 * moderation identity, the deep-link scroll target and its bespoke close
 * handler) come in as props.
 */
type Props = {
  controller: WasteEventActionsController;
  sites: SiteSummary[];
  defaultSiteId?: string | null;
  defaultKitchenId?: string | null;
  currentUserId: string | null;
  isModerator: boolean;
  scrollToCommentId?: string | null;
  // Transactions clears its deep-link URL params on close; the detail view just
  // nulls the panel. Defaults to the latter.
  onCloseComments?: () => void;
  // Embed the commented event's full card at the top of the panel (POS2-849) so
  // a notification opens straight to it — used by the list, where the panel's
  // event usually isn't a visible card. Off on the detail page (the page IS the
  // card). Needs the auto-classifying set for the card's resting-state flag.
  embedCommentsCard?: boolean;
  autoClassifyingSites?: ReadonlySet<string>;
};

export function WasteEventModals({
  controller: c,
  sites,
  defaultSiteId = null,
  defaultKitchenId = null,
  currentUserId,
  isModerator,
  scrollToCommentId = null,
  onCloseComments,
  embedCommentsCard = false,
  autoClassifyingSites = EMPTY_SITE_SET,
}: Props) {
  const commentsEvent = c.commentsEvent;
  // The commented event, rendered inline above the thread. No `onOpenComments`
  // — the Comments button is redundant inside the comments panel.
  const eventCard =
    embedCommentsCard && commentsEvent ? (
      <TransactionCard
        event={commentsEvent}
        bare
        onAnalyse={c.onAnalyse}
        onConfirm={c.onConfirm}
        onRelabel={c.setRelabelEvent}
        isConfirming={c.isConfirming(commentsEvent.id)}
        isAnalysing={c.isAnalysing(commentsEvent.id)}
        siteAutoClassifying={autoClassifyingSites.has(commentsEvent.site_id)}
        onExpandImage={c.setLightboxEvent}
        siteHasActiveMenu={
          sites.find((s) => s.id === commentsEvent.site_id)?.has_active_menu ?? false
        }
        onEdit={c.setEditEvent}
      />
    ) : undefined;

  return (
    <>
      <ImageLightbox
        open={c.lightboxEvent !== null}
        onOpenChange={(open) => {
          if (!open) c.setLightboxEvent(null);
        }}
        imageUrl={c.lightboxEvent?.image_read_url ?? null}
        beforeImageUrl={c.lightboxEvent?.before_image_read_url ?? null}
        eventId={c.lightboxEvent?.id ?? null}
        // POS2-1214: the crop box frames that device's bin. The placeholder did not come
        // from its camera, so applying it would cut the printed caption off here too.
        boundingArea={
          c.lightboxEvent && !isPlaceholderCapture(c.lightboxEvent)
            ? (c.lightboxEvent.bounding_area ?? null)
            : null
        }
      />

      <RelabelPicker
        event={c.relabelEvent}
        onClose={() => c.setRelabelEvent(null)}
        onSubmit={c.handleRelabelSubmit}
        isSubmitting={c.relabelIsSubmitting}
      />

      <LogWasteDialog
        open={c.logOpen || c.editEvent !== null}
        onClose={() => {
          c.setLogOpen(false);
          c.setEditEvent(null);
        }}
        sites={sites}
        defaultSiteId={defaultSiteId}
        defaultKitchenId={defaultKitchenId}
        editEvent={c.editEvent}
      />

      <CommentsPanel
        event={c.commentsEvent}
        currentUserId={currentUserId}
        isModerator={isModerator}
        onClose={onCloseComments ?? (() => c.setCommentsEvent(null))}
        scrollToCommentId={scrollToCommentId}
        eventCard={eventCard}
      />
    </>
  );
}
